import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createMobileUser } from "../../../entities/agent/model/agentSlice";
import { selectAgentState } from "../../../entities/agent/model/selectors";
import { selectSelectedAgency } from "../../../entities/agency/model/selectors";
import {
  persistLoggedUserSession,
  redirectToAppHome
} from "../../../shared/services/authSessionService";
import { getCurrentLanguageKey, getLanguageSection } from "../../../shared/services/languageService";

const DEFAULT_COUNTRY_ID = "254";
const DEFAULT_MOBILE_APP_NAME = "SunExcursions";

const createInitialFormState = (selectedAgency) => ({
  full_name: "",
  email: "",
  phone: "",
  city: "",
  agency: selectedAgency?.name || selectedAgency?.agency_name || "",
  password: "",
  confirm_password: "",
  terms: false,
  policies: false,
  error: "",
  success: ""
});

const splitFullName = (fullName) => {
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length <= 1) {
    return {
      firstName: parts[0] || "",
      lastName: ""
    };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ")
  };
};

const normalizePhoneParts = (selectedAgency) => {
  const raw = selectedAgency?.raw || {};
  const part1 = String(raw.Phone_1 || selectedAgency?.phone || "").trim();
  const part2 = String(raw.Phone_2 || selectedAgency?.phone2 || "").trim();
  const part3 = String(raw.Phone_3 || "").trim();

  return {
    part1,
    part2,
    part3,
    full: `${part1}${part2}${part3}`.trim()
  };
};

function AgentRegisterForm() {
  const defaultLanguage = useMemo(() => getLanguageSection("agentregister"), []);
  const formLanguage = defaultLanguage?.form || {};
  const currentLanguage = getCurrentLanguageKey();
  const multimediaTermsLink =
    currentLanguage === "esp"
      ? "https://marivalagents.com/es/terminos-y-condiciones-multimedia"
      : "https://marivalagents.com/multimedia-terms-and-conditions";
  const policyLinks = {
    cookie: currentLanguage === "esp" ? "/es/politica-de-cookies" : "/cookie-policy",
    privacy: currentLanguage === "esp" ? "/es/aviso-de-privacidad" : "/privacy-notice"
  };
  const dispatch = useDispatch();
  const selectedAgency = useSelector(selectSelectedAgency);
  const { creating, createError } = useSelector(selectAgentState);

  const [form, setForm] = useState(() => createInitialFormState(selectedAgency));

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      agency: selectedAgency?.name || selectedAgency?.agency_name || ""
    }));
  }, [selectedAgency]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      error: "",
      success: ""
    }));
  };

  const resetForm = () => {
    setForm({
      ...createInitialFormState(selectedAgency),
      success: formLanguage.success || "Registro enviado correctamente."
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      full_name,
      email,
      phone,
      password,
      confirm_password,
      terms,
      policies
    } = form;

    if (
      !full_name ||
      !email ||
      !phone ||
      !form.city ||
      !form.agency ||
      !password ||
      !confirm_password
    ) {
      setForm((prev) => ({
        ...prev,
        error: formLanguage.requiredFieldsError || "Por favor completa todos los campos."
      }));
      return;
    }

    if (password !== confirm_password) {
      setForm((prev) => ({
        ...prev,
        error: formLanguage.passwordMismatchError || "Las contraseñas no coinciden."
      }));
      return;
    }

    if (!terms) {
      setForm((prev) => ({
        ...prev,
        error: formLanguage.termsError || "Debes aceptar los términos y condiciones."
      }));
      return;
    }

    if (!policies) {
      setForm((prev) => ({
        ...prev,
        error: formLanguage.policiesError || "Debes aceptar el aviso de privacidad y la política de cookies."
      }));
      return;
    }

    const { firstName, lastName } = splitFullName(full_name);
    const agencyPhones = normalizePhoneParts(selectedAgency);

    const payload = {
      RegisterType: "Agent",
      Id_Country: String(selectedAgency?.raw?.Id_Country || DEFAULT_COUNTRY_ID),
      AgencyPhone1: agencyPhones.part1,
      AgencyPhone2: agencyPhones.part2,
      AgencyPhone3: agencyPhones.part3,
      OSSNCCRAMember: "0",
      AgencyPhone: agencyPhones.full,
      Id_Agency: String(selectedAgency?.id || ""),
      FirstName: firstName,
      LastName: lastName,
      Email: email.trim(),
      AgentPhone: phone.trim(),
      password,
      passwordv: confirm_password,
      terms: terms ? "on" : "",
      mobileAppName: DEFAULT_MOBILE_APP_NAME,
      isConflict: "0",
      marivalrewards:-1
    };

    setForm((prev) => ({
      ...prev,
      error: "",
      success: ""
    }));

    const resultAction = await dispatch(createMobileUser(payload));

    if (createMobileUser.fulfilled.match(resultAction)) {
      const createdUserResponse = resultAction.payload;
      const sessionData = createdUserResponse?.data || createdUserResponse;

      persistLoggedUserSession(sessionData);
      resetForm();
      redirectToAppHome();
      return;
    }

    setForm((prev) => ({
      ...prev,
      error: resultAction.payload || formLanguage.submitError || "Ocurrió un error al enviar el formulario.",
      success: ""
    }));
  };

  const inputClass = "mr-login-input h-11 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#11183f] shadow-none";
  const labelClass = "mb-2 block text-xs font-black uppercase tracking-wide text-slate-500";

  if (!selectedAgency) {
    return (
      <div className="mx-auto mt-6 max-w-5xl rounded-2xl border border-slate-200 bg-white p-5 shadow-marival md:p-6">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold leading-6 text-slate-600">
          {formLanguage.waitingAgencyMessage || "Paso 2. Selecciona o crea una agencia para habilitar el formulario de registro."}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-6 max-w-5xl rounded-2xl border border-slate-200 bg-white p-5 shadow-marival md:p-6">
      <div className="mb-6 text-center">
        <h1 className="m-0 text-2xl font-black text-[#11183f]">
          {formLanguage.title || "Paso 2. Completa tus datos"}
        </h1>
        <p className="m-0 mt-2 text-sm leading-6 text-slate-500">
          {formLanguage.subtitle || "Ingresa la información del agente para finalizar el registro y activar el acceso."}
        </p>
      </div>

      {(form.error || createError) ? (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold leading-6 text-red-700">{form.error || createError}</div>
      ) : null}

      {form.success ? (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold leading-6 text-emerald-700">{form.success}</div>
      ) : null}

      <form onSubmit={handleSubmit} method="GET">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
              <label className={labelClass}>{formLanguage.fullNameLabel || "Nombre completo"}</label>
              <input
                type="text"
                name="full_name"
                className={inputClass}
                value={form.full_name}
                onChange={handleChange}
                placeholder={formLanguage.fullNamePlaceholder || "Escribe tu nombre completo"}
              />
          </div>

          <div>
              <label className={labelClass}>{formLanguage.emailLabel || "Correo electrónico"}</label>
              <input
                type="email"
                name="email"
                className={inputClass}
                value={form.email}
                onChange={handleChange}
                placeholder={formLanguage.emailPlaceholder || "ejemplo@correo.com"}
              />
          </div>

          <div>
              <label className={labelClass}>{formLanguage.phoneLabel || "Teléfono"}</label>
              <input
                type="text"
                name="phone"
                className={inputClass}
                value={form.phone}
                onChange={handleChange}
                placeholder={formLanguage.phonePlaceholder || "Tu número de teléfono"}
              />
          </div>

          <div>
              <label className={labelClass}>{formLanguage.cityLabel || "Ciudad"}</label>
              <input
                type="text"
                name="city"
                className={inputClass}
                value={form.city}
                onChange={handleChange}
                placeholder={formLanguage.cityPlaceholder || "Tu ciudad"}
              />
          </div>

        <div className="md:col-span-2">
          <label className={labelClass}>{formLanguage.agencyLabel || "Agencia / Empresa"}</label>
          <input
            type="text"
            name="agency"
            className={`${inputClass} bg-slate-50 text-slate-500`}
            value={form.agency}
            readOnly
            placeholder={formLanguage.agencyPlaceholder || "Agencia seleccionada"}
          />
        </div>

          <div>
              <label className={labelClass}>{formLanguage.passwordLabel || "Contraseña"}</label>
              <input
                type="password"
                name="password"
                className={inputClass}
                value={form.password}
                onChange={handleChange}
                placeholder={formLanguage.passwordPlaceholder || "********"}
              />
          </div>

          <div>
              <label className={labelClass}>{formLanguage.confirmPasswordLabel || "Confirmar contraseña"}</label>
              <input
                type="password"
                name="confirm_password"
                className={inputClass}
                value={form.confirm_password}
                onChange={handleChange}
                placeholder={formLanguage.confirmPasswordPlaceholder || "********"}
              />
          </div>
        </div>

        <div className="mt-5 flex items-start gap-3 text-sm font-bold text-slate-600">
            <input
              id="agent-register-terms"
              type="checkbox"
              name="terms"
              checked={form.terms}
              onChange={handleChange}
              required
              className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#f6c833]"
            />
            <label htmlFor="agent-register-terms" className="m-0">
              {formLanguage.termsTextBefore || "He leído y acepto los términos y condiciones para el uso del "}
              <a href={multimediaTermsLink} target="_blank" rel="noreferrer" className="text-[#4aa6cf] underline hover:text-[#11183f]">
                {formLanguage.termsLinkText || "material multimedia de Marival Resorts"}
              </a>
              {formLanguage.termsTextAfter || "."}
            </label>
        </div>

        <div className="my-5 flex items-start gap-3 text-sm font-bold text-slate-600">
            <input
              id="agent-register-policies"
              type="checkbox"
              name="policies"
              checked={form.policies}
              onChange={handleChange}
              required
              className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#f6c833]"
            />
            <label htmlFor="agent-register-policies" className="m-0">
              {formLanguage.policiesTextBefore || "He leído y acepto los términos del "}
              <a href={policyLinks.privacy} target="_blank" rel="noreferrer" className="text-[#4aa6cf] underline hover:text-[#11183f]">
                {formLanguage.policiesPrivacyLink || "Aviso de Privacidad"}
              </a>
              {formLanguage.policiesTextMiddle || " y la "}
              <a href={policyLinks.cookie} target="_blank" rel="noreferrer" className="text-[#4aa6cf] underline hover:text-[#11183f]">
                {formLanguage.policiesCookieLink || "Política de Cookies"}
              </a>
              {formLanguage.policiesTextAfter || "."}
            </label>
        </div>

        <button
          type="submit"
          className="inline-flex h-12 w-full items-center justify-center rounded-xl border-0 bg-[#f6c833] text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d] disabled:cursor-wait disabled:opacity-70"
          disabled={creating}
        >
          {creating
            ? (formLanguage.submitting || "Enviando...")
            : (formLanguage.submitButton || "Crear cuenta")}
        </button>
      </form>
    </div>
  );
}

export default AgentRegisterForm;
